import {
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import {
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDialogContext,
  TuiError,
  TuiGroup,
  TuiLabel,
  TuiTextfield,
} from '@taiga-ui/core';
import { injectContext } from '@taiga-ui/polymorpheus';
import { PrescriptionFormDialogData } from '../../models/prescription-form-dialog-data';
import {
  NgxExtendedPdfViewerComponent,
  NgxExtendedPdfViewerModule,
} from 'ngx-extended-pdf-viewer';
import { TuiCardLarge } from '@taiga-ui/layout';
import {
  TuiComboBoxModule,
  TuiInputModule,
  TuiTextfieldControllerModule,
} from '@taiga-ui/legacy';
import {
  TuiAvatar,
  TuiBlock,
  TuiDataListWrapper,
  TuiFieldErrorPipe,
  TuiFilterByInputPipe,
  tuiItemsHandlersProvider,
  TuiRadio,
  TuiSwitch,
} from '@taiga-ui/kit';
import { User } from '../../models/user';
import { Patient } from '../../models/patient';
import { Organisme } from '../../models/organisme';
import { PrescriptionForm } from '../../models/prescriptionForm/prescriptionForm';
import { DepInformationsComplementaires } from '../../models/prescriptionForm/depInformationsComplementaires';
import { PrescriptionFormService } from '../../services/prescription-form.service';
import { AuthService } from '../../services/auth.service';
import { AsyncPipe, DatePipe } from '@angular/common';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { DepInformationsForm } from '../../models/prescriptionForm/depInformationsForm';
import { TuiDay } from '@taiga-ui/cdk';
import { Prescription } from '../../models/prescription';

@Component({
  selector: 'app-prescription-form-autre',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TuiCardLarge,
    TuiAppearance,
    NgxExtendedPdfViewerModule,
    TuiButton,
    TuiComboBoxModule,
    TuiDataList,
    TuiDataListWrapper,
    TuiFilterByInputPipe,
    TuiError,
    AsyncPipe,
    TuiSwitch,
    TuiTextfield,
    TuiFieldErrorPipe,
    TuiLabel,
    TuiInputModule,
    TuiTextfieldControllerModule,
    TuiAvatar,
    TuiGroup,
    TuiRadio,
    TuiBlock,
  ],
  templateUrl: './prescription-form-autre.component.html',
  styleUrl: './prescription-form-autre.component.scss',
  providers: [
    tuiItemsHandlersProvider({
      stringify: (item: User | string) =>
        item instanceof User ? `${item.nom} ${item.prenom}` : item,
    }),
  ],
})
export class PrescriptionFormAutreComponent implements OnInit {
  public readonly context =
    injectContext<TuiDialogContext<void, PrescriptionFormDialogData | void>>();

  @ViewChild(NgxExtendedPdfViewerComponent)
  pdfViewerComponent!: NgxExtendedPdfViewerComponent;
  private prescriptionFormService = inject(PrescriptionFormService);
  protected authService = inject(AuthService);

  public etatPush: undefined | boolean | 'loading';

  public patient!: Patient | undefined;
  public prescription!: Prescription | undefined;
  public codeDActivite!: string;
  public userList!: User[];
  public organisme!: Organisme | null;
  public reedition!: boolean;
  public prescriptionFormPrecedente!: PrescriptionForm | undefined;
  public prescriptionFormPrecedenteInformationsComplementaires!:
    | DepInformationsComplementaires
    | undefined;

  public formPdf: any = {};
  public form!: FormGroup;

  public valeurNonValidePourList: WritableSignal<string[] | null> =
    signal(null);

  ngOnInit(): void {
    this.patient = this.context.data!.patient;
    this.prescription = this.context.data!.prescription;
    this.codeDActivite = this.context.data!.codeDActivite;
    this.userList = this.context.data!.userList;
    this.organisme = this.context.data!.organisme;
    this.reedition = this.context.data!.reedition;
    this.prescriptionFormPrecedente =
      this.context.data!.prescriptionFormPrecedente;
    this.prescriptionFormPrecedenteInformationsComplementaires =
      this.prescriptionFormPrecedente?.informationsComplementaires instanceof
      DepInformationsComplementaires
        ? this.prescriptionFormPrecedente.informationsComplementaires
        : undefined;

    let user = this.authService.getUser();

    this.formPdf = {
      dep_type_slug:
        this.patient && this.prescription?.type == 'prolongation'
          ? 'prolongation'
          : '3mois',
      date_de_prescription: new DatePipe('fr-FR').transform(
        this.prescription?.dateDebut ?? new Date(),
        'dd/MM/yyyy',
      ),
      nir_court: this.patient?.nir?.slice(0, 13),
      nir_cle: this.patient?.nir?.slice(-2),
      assure_nom: this.patient?.assure?.nom,
      assure_prenom: this.patient?.assure?.prenom,
      assure_adresse: (() => {
        if (this.patient?.assure) {
          let adresse: string = this.patient.assure.adresse ?? '';
          if (this.patient.assure.adresse2) {
            adresse += ' ' + this.patient.assure.adresse2;
          }
          if (this.patient.assure.adresse3) {
            adresse += ' ' + this.patient.assure.adresse3;
          }
          if (this.patient.assure.codePostal) {
            adresse += ' ' + this.patient.assure.codePostal;
          }
          if (this.patient.assure.ville) {
            adresse += ' ' + this.patient.assure.ville;
          }
          return adresse;
        } else {
          let adresse: string = this.patient?.adresse?.ligne1 ?? '';
          if (this.patient?.adresse?.ligne2) {
            adresse += ' ' + this.patient?.adresse?.ligne2;
          }
          if (this.patient?.adresse?.ligne3) {
            adresse += ' ' + this.patient?.adresse?.ligne3;
          }
          if (this.patient?.adresse?.codePostal) {
            adresse += ' ' + this.patient?.adresse?.codePostal;
          }
          if (this.patient?.adresse?.ville) {
            adresse += ' ' + this.patient?.adresse?.ville;
          }
          return adresse;
        }
      })(),
      caisse: this.organisme
        ? `${this.organisme.nom} (${this.organisme.numero})`
        : null,
      assure_situation: null,
      assure_sans_emploi_date: '',
      assure_autre_detail: '',

      accident: this.prescription?.accident ? '1' : null,
      accident_date: new DatePipe('fr').transform(
        this.prescription?.dateAccident,
        'dd/MM/yyyy',
      ),
      pensionne_de_guerre: this.prescription?.pensionDeGuerre ?? null,
      nom:
        this.patient?.nom == this.patient?.assure?.nom ? '' : this.patient?.nom,
      prenom:
        this.patient?.prenom == this.patient?.assure?.prenom
          ? ''
          : this.patient?.prenom,
      sexe:
        this.patient?.sexe == 'Homme'
          ? 'homme'
          : this.patient?.sexe == 'Femme'
            ? 'femme'
            : null,
      date_de_naissance: this.patient?.dateDeNaissance
        ? new DatePipe('fr').transform(
            this.patient?.dateDeNaissance,
            'dd/MM/yyyy',
          )
        : null,
      lien_avec_l_assure: null,
      activite_professionnelle_ou_pension: this.prescription?.couvertureSante
        ? '1'
        : null,

      ald_sur_liste: this.prescription?.affectionLongueDureeReconnueSurListe
        ? '1'
        : null,
      ald_hors_liste: this.prescription?.affectionLongueDureeReconnueHorsListe
        ? '1'
        : null,
      oxygenotherapie_par_concentrateur:
        this.prescription?.oxygenotherapieParConcentrateur ?? false,
      oxygenotherapie_par_concentrateur_poste_fixe:
        this.prescription?.posteFixeOxygenotherapieParConcentrateur ?? false,
      oxygenotherapie_par_concentrateur_deambulation:
        this.prescription?.deambulationOxygenotherapieParConcentrateur ?? false,
      oxygenotherapie_par_concentrateur_debit:
        this.prescription?.debitOxygenotherapieParConcentrateur ?? '',
      oxygenotherapie_par_concentrateur_duree:
        this.prescription?.dureeOxygenotherapieParConcentrateur ?? '',
      oxygenotherapie_gazeuse_par_bouteille:
        this.prescription?.oxygenotherapieGazeuseParBouteille ?? false,
      oxygenotherapie_gazeuse_par_bouteille_poste_fixe:
        this.prescription?.posteFixeOxygenotherapieGazeuseParBouteille ?? false,
      oxygenotherapie_gazeuse_par_bouteille_deambulation:
        this.prescription?.deambulationOxygenotherapieGazeuseParBouteille ??
        false,
      oxygenotherapie_gazeuse_par_bouteille_debit:
        this.prescription?.debitOxygenotherapieGazeuseParBouteille ?? '',
      oxygenotherapie_gazeuse_par_bouteille_duree:
        this.prescription?.dureeOxygenotherapieGazeuseParBouteille ?? '',
      oxygenotherapie_liquide:
        this.prescription?.oxygenotherapieLiquide ?? false,
      oxygenotherapie_liquide_poste_fixe:
        this.prescription?.posteFixeOxygenotherapieLiquide ?? false,
      oxygenotherapie_liquide_deambulation:
        this.prescription?.deambulationOxygenotherapieLiquide ?? false,
      oxygenotherapie_liquide_debit:
        this.prescription?.debitOxygenotherapieLiquide ?? '',
      oxygenotherapie_liquide_duree:
        this.prescription?.dureeOxygenotherapieLiquide ?? '',
      ventilation_mecanique_a_l_embout_buccal:
        this.prescription?.ventilationMecaniqueALEmboutBuccal ?? false,
      ventilation_mecanique_a_l_embout_buccal_debit:
        this.prescription?.debitVentilationMecaniqueALEmboutBuccal ?? '',
      ventilation_mecanique_a_l_embout_buccal_duree:
        this.prescription?.dureeVentilationMecaniqueALEmboutBuccal ?? '',
      ventilation_mecanique_par_masque_nasal:
        this.prescription?.ventilationMecaniqueParMasqueNasal ?? false,
      ventilation_mecanique_par_masque_nasal_debit:
        this.prescription?.debitVentilationMecaniqueParMasqueNasal ?? '',
      ventilation_mecanique_par_masque_nasal_duree:
        this.prescription?.dureeVentilationMecaniqueParMasqueNasal ?? '',
      ventilation_mecanique_par_tracheotomie:
        this.prescription?.ventilationMecaniqueParTracheotomie ?? false,
      ventilation_mecanique_par_tracheotomie_debit:
        this.prescription?.debitVentilationMecaniqueParTracheotomie ?? '',
      ventilation_mecanique_par_tracheotomie_duree:
        this.prescription?.dureeVentilationMecaniqueParTracheotomie ?? '',
      ventilation_mecanique_par_prothese_extra_thoracique:
        this.prescription?.ventilationMecaniqueParProtheseExtraThoracique ??
        false,
      ventilation_mecanique_par_prothese_extra_thoracique_debit:
        this.prescription
          ?.debitVentilationMecaniqueParProtheseExtraThoracique ?? '',
      ventilation_mecanique_par_prothese_extra_thoracique_duree:
        this.prescription
          ?.dureeVentilationMecaniqueParProtheseExtraThoracique ?? '',
      ventilation_mecanique_par_ppc:
        this.prescription?.ventilationMecaniqueParPressionPositiveContinue ??
        false,
      ventilation_mecanique_par_ppc_debit:
        this.prescription
          ?.debitVentilationMecaniqueParPressionPositiveContinue ?? '',
      ventilation_mecanique_par_ppc_duree:
        this.prescription
          ?.dureeVentilationMecaniqueParPressionPositiveContinue ?? '',
      aspiration_sans_ventilation_sur_tracheotomie:
        this.prescription?.aspirationSansVentilationSurTracheotomie ?? false,
      autres: this.prescription?.autre ?? false,
      autres_detail: '',

      appareillage_prescrit: 'a_l_acte',
      association_adresse: '',
      association_forfait_soins_numero: this.prescription?.numeroForfait ?? '',
      association_nom: this.prescription?.nomAssociation ?? '',
      concentrateur_marque:
        this.prescription?.marqueOxygenotherapieParConcentrateur ?? '',
      concentrateur_type:
        this.prescription?.typeOxygenotherapieParConcentrateur ?? '',
      materiel_O2_liquide_marque: this.prescription?.marqueRespirateur ?? '',
      materiel_O2_liquide_type: this.prescription?.typeRespirateur ?? '',
      respirateur_marque: this.prescription?.marqueOxygenotherapieLiquide ?? '',
      respirateur_type: this.prescription?.typeOxygenotherapieLiquide ?? '',
      autres_marque: this.prescription?.marqueAutre ?? '',
      autres_type: this.prescription?.typeAutre ?? '',
      prescripteur_type: 'pneumologue',
      prescripteur_type_autre_detail: '',

      bronchite_chronique_principale: null,
      bronchite_chronique_secondaire: null,
      lesions_mutilantes_post_tuberculeuses_principale: null,
      lesions_mutilantes_post_tuberculeuses_secondaire: null,
      asthme_a_dyspnee_continue_principale: null,
      asthme_a_dyspnee_continue_secondaire: null,
      fibroses_et_processus_interstitiels_principale: null,
      fibroses_et_processus_interstitiels_secondaire: null,
      cypho_scoliose_principale: null,
      cypho_scoliose_secondaire: null,
      emphyseme_primitif_principale: null,
      emphyseme_primitif_secondaire: null,
      dilatations_etendues_des_bronches_principale: null,
      dilatations_etendues_des_bronches_secondaire: null,
      myopathies_principale: null,
      myopathies_secondaire: null,
      autres_atteintes_neuro_musculaires_principale: null,
      autres_atteintes_neuro_musculaires_secondaire: null,
      pneumoconioses_principale: null,
      pneumoconioses_secondaire: null,
      apnees_du_sommeil_principale: null,
      apnees_du_sommeil_secondaire: null,
      mucoviscidose_principale: null,
      mucoviscidose_secondaire: null,
      dysplasie_broncho_pulmonaire_du_nourrisson_principale: null,
      dysplasie_broncho_pulmonaire_du_nourrisson_secondaire: null,
      autre_principale: null,
      autre_secondaire: null,
      autre_detail: '',

      tabac: this.patient?.tableauDeBord?.arretTabac
        ? this.patient?.tableauDeBord?.arretTabac?.value == 'OUI' ||
          this.patient?.tableauDeBord?.arretTabac?.date
          ? 'ne_fume_plus'
          : 'fume'
        : 'jamais',
      antecedent_ivd: null,
      ivd_actuelle: null,
      spirographie_date: '',
      spirographie_poids: '',
      spirographie_taille: '',
      spirographie_capacite_vitale: '',
      spirographie_capacite_totale: '',
      spirographie_vems: '',
      gaz_du_sang_arteriel: null,

      repos_en_air_mesure_1_date: '',
      repos_en_air_mesure_1_paco2: '',
      repos_en_air_mesure_1_pao2: '',
      repos_en_air_mesure_1_ph: '',
      repos_en_air_mesure_2_date: '',
      repos_en_air_mesure_2_paco2: '',
      repos_en_air_mesure_2_pao2: '',
      repos_en_air_mesure_2_ph: '',
      repos_sous_o2_et_ou_ventilation_date: '',
      repos_sous_o2_et_ou_ventilation_paco2: '',
      repos_sous_o2_et_ou_ventilation_pao2: '',
      repos_sous_o2_et_ou_ventilation_ph: '',
      a_l_effort_en_air_date: '',
      a_l_effort_en_air_paco2: '',
      a_l_effort_en_air_pao2: '',
      a_l_effort_en_air_ph: '',
      a_l_effort_en_air_sao2: '',

      hematocrite: '',
      cas_particuliers: (() => {
        let cas_particuliers: string = this.patient?.tableauDeBord?.obs?.value
          ? `OBS = ${this.patient?.tableauDeBord?.obs?.value}\n`
          : '';

        cas_particuliers += this.patient?.tableauDeBord?.iahResiduel?.value
          ? `IAH  = ${this.patient?.tableauDeBord?.iahResiduel?.value}`
          : '';

        return cas_particuliers;
      })(),
    };

    this.form = new FormGroup({
      typeSlug: new FormControl('dep', [
        Validators.required,
        Validators.pattern('^dep$'),
      ]),
      telephone: new FormControl(
        this.patient?.mobile ?? this.patient?.telephone ?? null,
        [Validators.required],
      ),
      medecin: new FormControl(
        (() => {
          if (
            this.userList.find(
              (test) => test.id == this.authService.getUser()?.id,
            )
          ) {
            return user;
          } else {
            return null;
          }
        })(),
        [Validators.required],
      ), // different
      userId: new FormControl(user?.id, [
        Validators.required,
        Validators.pattern('^[0-9]+$'),
      ]),
      codeDActivite: new FormControl(this.codeDActivite, [Validators.required]),
      aSigner: new FormControl(
        (() => {
          if (
            this.patient?.canSign ||
            (!this.patient && this.authService.getUser()?.rpps)
          ) {
            return true;
          } else {
            return false;
          }
        })(),
      ),
    });
  }

  selectMe() {
    this.form.get('medecin')?.setValue(this.authService.getUser());
  }

  protected readonly matcherString = (user: User, search: string): boolean =>
    (`${user.prenom}`
      .split(' ')
      .pop()
      ?.toLowerCase()
      .startsWith(search.toLowerCase()) ||
      `${user.nom}`
        .split(' ')
        .pop()
        ?.toLowerCase()
        .startsWith(search.toLowerCase())) ??
    false;

  initEtatPush(): void {
    this.etatPush = undefined;
  }

  async onSubmit() {
    if (this.prescriptionFormPrecedente && this.reedition) {
      this.prescriptionFormService
        .delete(this.prescriptionFormPrecedente)
        .subscribe({
          next: async (data) => {
            await this.post();
          },
          error: (error) => {
            console.log(error);
          },
        });
    } else {
      await this.post();
    }
  }

  async post() {
    let formPdfFormate = this.formatFormPdf(this.formPdf);

    this.valeurNonValidePourList.set(this.validatorFormPdf(formPdfFormate));

    let user = this.authService.getUser();

    if (this.patient) {
      Object.keys(this.form.controls).forEach((key) => {
        const control = this.form.get(key);
        if (
          control?.errors?.['required'] &&
          (control.value === null || control.value === '')
        ) {
          control.setValue('Ø');
          control.updateValueAndValidity();
        }
      });
    }

    if (this.valeurNonValidePourList() || !user) {
      this.form.markAllAsTouched();
    } else {
      this.form.markAllAsTouched();
      if (this.form.invalid) {
        console.error('Formulaire invalide', this.form.value);
        return;
      }
      this.etatPush = 'loading';

      let prescriptionForm = new PrescriptionForm(
        undefined,
        'dep',
        undefined,
        this.form.value.medecin.id,
        undefined,
        user!.id,
        undefined,
        this.formPdf.nir_court + this.formPdf.nir_cle,
        this.codeDActivite,
        formPdfFormate.date_de_prescription,
        formPdfFormate.nom ?? formPdfFormate.assure_nom,
        formPdfFormate.prenom ?? formPdfFormate.assure_prenom,
        formPdfFormate.sexe,
        formPdfFormate.date_de_naissance,
        formPdfFormate.assure_adresse,
        null,
        undefined,
        undefined,
        this.form.value.telephone,
        this.form.value.aSigner ?? false,
        this.form.value.aSigner ?? false,
        null,
        undefined,
        this.prescription?.id,
        new DepInformationsComplementaires(formPdfFormate.dep_type_slug),
        new DepInformationsForm(
          formPdfFormate.nom ? formPdfFormate.assure_nom : null,
          formPdfFormate.prenom ? formPdfFormate.assure_prenom : null,
          formPdfFormate.assure_adresse,
          formPdfFormate.caisse,
          formPdfFormate.assure_situation,
          formPdfFormate.assure_sans_emploi_date,
          formPdfFormate.assure_autre_detail,
          formPdfFormate.accident,
          formPdfFormate.accident_date,
          formPdfFormate.pensionne_de_guerre,
          formPdfFormate.lien_avec_l_assure,
          formPdfFormate.activite_professionnelle_ou_pension,
          formPdfFormate.prescripteur_type,
          formPdfFormate.prescripteur_type_autre_detail,
          formPdfFormate.ald_sur_liste,
          formPdfFormate.ald_hors_liste,
          formPdfFormate.oxygenotherapie_par_concentrateur,
          formPdfFormate.oxygenotherapie_par_concentrateur_poste_fixe,
          formPdfFormate.oxygenotherapie_par_concentrateur_deambulation,
          formPdfFormate.oxygenotherapie_par_concentrateur_duree,
          formPdfFormate.oxygenotherapie_par_concentrateur_debit,
          formPdfFormate.oxygenotherapie_gazeuse_par_bouteille,
          formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_poste_fixe,
          formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_deambulation,
          formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_duree,
          formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_debit,
          formPdfFormate.oxygenotherapie_liquide,
          formPdfFormate.oxygenotherapie_liquide_poste_fixe,
          formPdfFormate.oxygenotherapie_liquide_deambulation,
          formPdfFormate.oxygenotherapie_liquide_duree,
          formPdfFormate.oxygenotherapie_liquide_debit,
          formPdfFormate.ventilation_mecanique_a_l_embout_buccal,
          formPdfFormate.ventilation_mecanique_a_l_embout_buccal_duree,
          formPdfFormate.ventilation_mecanique_a_l_embout_buccal_debit,
          formPdfFormate.ventilation_mecanique_par_masque_nasal,
          formPdfFormate.ventilation_mecanique_par_masque_nasal_duree,
          formPdfFormate.ventilation_mecanique_par_masque_nasal_debit,
          formPdfFormate.ventilation_mecanique_par_tracheotomie,
          formPdfFormate.ventilation_mecanique_par_tracheotomie_duree,
          formPdfFormate.ventilation_mecanique_par_tracheotomie_debit,
          formPdfFormate.ventilation_mecanique_par_prothese_extra_thoracique,
          formPdfFormate.ventilation_mecanique_par_prothese_extra_thoracique_duree,
          formPdfFormate.ventilation_mecanique_par_prothese_extra_thoracique_debit,
          formPdfFormate.ventilation_mecanique_par_ppc,
          formPdfFormate.ventilation_mecanique_par_ppc_duree,
          formPdfFormate.ventilation_mecanique_par_ppc_debit,
          formPdfFormate.aspiration_sans_ventilation_sur_tracheotomie,
          formPdfFormate.autres,
          formPdfFormate.autres_detail,
          formPdfFormate.appareillage_prescrit,
          formPdfFormate.association_nom,
          formPdfFormate.association_adresse,
          formPdfFormate.association_forfait_soins_numero,
          formPdfFormate.concentrateur_marque,
          formPdfFormate.concentrateur_type,
          formPdfFormate.respirateur_marque,
          formPdfFormate.respirateur_type,
          formPdfFormate.materiel_O2_liquide_marque,
          formPdfFormate.materiel_O2_liquide_type,
          formPdfFormate.autres_marque,
          formPdfFormate.autres_type,
          formPdfFormate.bronchite_chronique_principale,
          formPdfFormate.bronchite_chronique_secondaire,
          formPdfFormate.lesions_mutilantes_post_tuberculeuses_principale,
          formPdfFormate.lesions_mutilantes_post_tuberculeuses_secondaire,
          formPdfFormate.asthme_a_dyspnee_continue_principale,
          formPdfFormate.asthme_a_dyspnee_continue_secondaire,
          formPdfFormate.fibroses_et_processus_interstitiels_principale,
          formPdfFormate.fibroses_et_processus_interstitiels_secondaire,
          formPdfFormate.cypho_scoliose_principale,
          formPdfFormate.cypho_scoliose_secondaire,
          formPdfFormate.emphyseme_primitif_principale,
          formPdfFormate.emphyseme_primitif_secondaire,
          formPdfFormate.dilatations_etendues_des_bronches_principale,
          formPdfFormate.dilatations_etendues_des_bronches_secondaire,
          formPdfFormate.myopathies_principale,
          formPdfFormate.myopathies_secondaire,
          formPdfFormate.autres_atteintes_neuro_musculaires_principale,
          formPdfFormate.autres_atteintes_neuro_musculaires_secondaire,
          formPdfFormate.pneumoconioses_principale,
          formPdfFormate.pneumoconioses_secondaire,
          formPdfFormate.apnees_du_sommeil_principale,
          formPdfFormate.apnees_du_sommeil_secondaire,
          formPdfFormate.mucoviscidose_principale,
          formPdfFormate.mucoviscidose_secondaire,
          formPdfFormate.dysplasie_broncho_pulmonaire_du_nourrisson_principale,
          formPdfFormate.dysplasie_broncho_pulmonaire_du_nourrisson_secondaire,
          formPdfFormate.autre_principale,
          formPdfFormate.autre_secondaire,
          formPdfFormate.autre_detail,
          formPdfFormate.tabac,
          formPdfFormate.antecedent_ivd,
          formPdfFormate.ivd_actuelle,
          formPdfFormate.spirographie_date,
          formPdfFormate.spirographie_poids,
          formPdfFormate.spirographie_taille,
          formPdfFormate.spirographie_capacite_vitale,
          formPdfFormate.spirographie_capacite_totale,
          formPdfFormate.spirographie_vems,
          formPdfFormate.gaz_du_sang_arteriel,
          formPdfFormate.repos_en_air_mesure_1_date,
          formPdfFormate.repos_en_air_mesure_1_ph,
          formPdfFormate.repos_en_air_mesure_1_paco2,
          formPdfFormate.repos_en_air_mesure_1_pao2,
          formPdfFormate.repos_en_air_mesure_2_date,
          formPdfFormate.repos_en_air_mesure_2_ph,
          formPdfFormate.repos_en_air_mesure_2_paco2,
          formPdfFormate.repos_en_air_mesure_2_pao2,
          formPdfFormate.repos_sous_o2_et_ou_ventilation_date,
          formPdfFormate.repos_sous_o2_et_ou_ventilation_ph,
          formPdfFormate.repos_sous_o2_et_ou_ventilation_paco2,
          formPdfFormate.repos_sous_o2_et_ou_ventilation_pao2,
          formPdfFormate.a_l_effort_en_air_date,
          formPdfFormate.a_l_effort_en_air_ph,
          formPdfFormate.a_l_effort_en_air_paco2,
          formPdfFormate.a_l_effort_en_air_pao2,
          formPdfFormate.a_l_effort_en_air_sao2,
          formPdfFormate.hematocrite,
          formPdfFormate.cas_particuliers,
        ),
      );

      this.prescriptionFormService.push(prescriptionForm).subscribe({
        next: (data) => {
          this.etatPush = true;
        },
        error: (error) => {
          this.etatPush = false;
        },
      });
    }
  }

  validatorFormPdf(objetAValider: any): string[] | null {
    let valeurNonValidePour: string[] = [];

    if (
      objetAValider.dep_type_slug === null ||
      !['3mois', 'modifie', 'prolongation'].includes(
        objetAValider.dep_type_slug,
      )
    ) {
      valeurNonValidePour.push('• TYPE DE DEP');
    }
    if (
      objetAValider.date_de_prescription === false ||
      objetAValider.date_de_prescription === null
    ) {
      valeurNonValidePour.push('• DATE DE PRESCRIPTION');
    }

    if (objetAValider.nir_court === null || objetAValider.nir_cle === null) {
      valeurNonValidePour.push('• N° D’IMMATRICULATION');
    }
    if (objetAValider.assure_nom === null) {
      valeurNonValidePour.push('• NOM');
    }
    if (objetAValider.assure_prenom === null) {
      valeurNonValidePour.push('• PRÉNOM');
    }
    if (
      ![
        null,
        'activite_salarie',
        'activite_non_salarie',
        'sans_emploi',
        'pensionne',
        'autre',
      ].includes(objetAValider.assure_situation)
    ) {
      valeurNonValidePour.push("• SITUATION DE L'ASSURE");
    }
    if (objetAValider.assure_sans_emploi_date === false) {
      valeurNonValidePour.push('• DATE DE CESSATION D’ACTIVITÉ');
    }

    if (![true, false, null].includes(objetAValider.accident)) {
      valeurNonValidePour.push('• ACCIDENT');
    }
    if (objetAValider.accident_date === false) {
      valeurNonValidePour.push('• DATE DE L’ACCIDENT');
    }
    if (![true, false].includes(objetAValider.pensionne_de_guerre)) {
      valeurNonValidePour.push('• PENSIONNE DE GUERRE');
    }
    if (![null, 'homme', 'femme'].includes(objetAValider.sexe)) {
      valeurNonValidePour.push('• SEXE');
    }
    if (objetAValider.date_de_naissance === false) {
      valeurNonValidePour.push('• DATE DE NAISSANCE');
    }
    if (
      ![
        null,
        'conjoint',
        'enfant',
        'autre_membre_famille',
        'personne_vivant_maritalement_avec_l_assure',
      ].includes(objetAValider.lien_avec_l_assure)
    ) {
      valeurNonValidePour.push("• LIEN AVEC L'ASSURE");
    }
    if (
      ![true, false, null].includes(
        objetAValider.activite_professionnelle_ou_pension,
      )
    ) {
      valeurNonValidePour.push('• ACTIVITÉ PROFESSIONNELLE OU PENSION');
    }

    if (![true, false, null].includes(objetAValider.ald_sur_liste)) {
      valeurNonValidePour.push('• ALD SUR LISTE');
    }
    if (![true, false, null].includes(objetAValider.ald_hors_liste)) {
      valeurNonValidePour.push('• ALD HORS LISTE');
    }

    if (
      ![true, false].includes(
        objetAValider.aspiration_sans_ventilation_sur_tracheotomie,
      )
    ) {
      valeurNonValidePour.push(
        '• ASPIRATION SANS VENTILATION SUR TRACHEOTOMIE',
      );
    }
    if (![true, false].includes(objetAValider.autres)) {
      valeurNonValidePour.push('• AUTRES TRAITEMENTS PRESCRITS');
    }

    if (
      ![null, 'a_l_acte', 'association'].includes(
        objetAValider.appareillage_prescrit,
      )
    ) {
      valeurNonValidePour.push('• APPAREILLAGE PRESCRIT');
    }
    if (
      ![null, 'pneumologue', 'reanimateur', 'generaliste', 'autre'].includes(
        objetAValider.prescripteur_type,
      )
    ) {
      valeurNonValidePour.push('• TYPE DE PRESCRIPTEUR');
    }

    if (
      ![true, false].includes(objetAValider.bronchite_chronique_principale) ||
      ![true, false].includes(
        objetAValider.lesions_mutilantes_post_tuberculeuses_principale,
      ) ||
      ![true, false].includes(
        objetAValider.asthme_a_dyspnee_continue_principale,
      ) ||
      ![true, false].includes(
        objetAValider.fibroses_et_processus_interstitiels_principale,
      ) ||
      ![true, false].includes(objetAValider.cypho_scoliose_principale) ||
      ![true, false].includes(objetAValider.emphyseme_primitif_principale) ||
      ![true, false].includes(
        objetAValider.dilatations_etendues_des_bronches_principale,
      ) ||
      ![true, false].includes(objetAValider.myopathies_principale) ||
      ![true, false].includes(
        objetAValider.autres_atteintes_neuro_musculaires_principale,
      ) ||
      ![true, false].includes(objetAValider.pneumoconioses_principale) ||
      ![true, false].includes(objetAValider.apnees_du_sommeil_principale) ||
      ![true, false].includes(objetAValider.mucoviscidose_principale) ||
      ![true, false].includes(
        objetAValider.dysplasie_broncho_pulmonaire_du_nourrisson_principale,
      ) ||
      ![true, false].includes(objetAValider.autre_principale)
    ) {
      valeurNonValidePour.push(
        '• ÉTIOLOGIE DE L’INSUFFISANCE RESPIRATOIRE (Principale)',
      );
    } else {
      let nombrePrincipale: number = 0;

      if (objetAValider.bronchite_chronique_principale === true)
        nombrePrincipale++;
      if (
        objetAValider.lesions_mutilantes_post_tuberculeuses_principale === true
      )
        nombrePrincipale++;
      if (objetAValider.asthme_a_dyspnee_continue_principale === true)
        nombrePrincipale++;
      if (objetAValider.fibroses_et_processus_interstitiels_principale === true)
        nombrePrincipale++;
      if (objetAValider.cypho_scoliose_principale === true) nombrePrincipale++;
      if (objetAValider.emphyseme_primitif_principale === true)
        nombrePrincipale++;
      if (objetAValider.dilatations_etendues_des_bronches_principale === true)
        nombrePrincipale++;
      if (objetAValider.myopathies_principale === true) nombrePrincipale++;
      if (objetAValider.autres_atteintes_neuro_musculaires_principale === true)
        nombrePrincipale++;
      if (objetAValider.pneumoconioses_principale === true) nombrePrincipale++;
      if (objetAValider.apnees_du_sommeil_principale === true)
        nombrePrincipale++;
      if (objetAValider.mucoviscidose_principale === true) nombrePrincipale++;
      if (
        objetAValider.dysplasie_broncho_pulmonaire_du_nourrisson_principale ===
        true
      )
        nombrePrincipale++;
      if (objetAValider.autre_principale === true) nombrePrincipale++;

      if (nombrePrincipale > 1) {
        valeurNonValidePour.push(
          '• ÉTIOLOGIE DE L’INSUFFISANCE RESPIRATOIRE (Principale)',
        );
      }
    }

    if (
      ![true, false].includes(objetAValider.bronchite_chronique_secondaire) ||
      ![true, false].includes(
        objetAValider.lesions_mutilantes_post_tuberculeuses_secondaire,
      ) ||
      ![true, false].includes(
        objetAValider.asthme_a_dyspnee_continue_secondaire,
      ) ||
      ![true, false].includes(
        objetAValider.fibroses_et_processus_interstitiels_secondaire,
      ) ||
      ![true, false].includes(objetAValider.cypho_scoliose_secondaire) ||
      ![true, false].includes(objetAValider.emphyseme_primitif_secondaire) ||
      ![true, false].includes(
        objetAValider.dilatations_etendues_des_bronches_secondaire,
      ) ||
      ![true, false].includes(objetAValider.myopathies_secondaire) ||
      ![true, false].includes(
        objetAValider.autres_atteintes_neuro_musculaires_secondaire,
      ) ||
      ![true, false].includes(objetAValider.pneumoconioses_secondaire) ||
      ![true, false].includes(objetAValider.apnees_du_sommeil_secondaire) ||
      ![true, false].includes(objetAValider.mucoviscidose_secondaire) ||
      ![true, false].includes(
        objetAValider.dysplasie_broncho_pulmonaire_du_nourrisson_secondaire,
      ) ||
      ![true, false].includes(objetAValider.autre_secondaire)
    ) {
      valeurNonValidePour.push(
        '• ÉTIOLOGIE DE L’INSUFFISANCE RESPIRATOIRE (Secondaire)',
      );
    } else {
      let nombreSecondaire: number = 0;

      if (objetAValider.bronchite_chronique_secondaire === true)
        nombreSecondaire++;
      if (
        objetAValider.lesions_mutilantes_post_tuberculeuses_secondaire === true
      )
        nombreSecondaire++;
      if (objetAValider.asthme_a_dyspnee_continue_secondaire === true)
        nombreSecondaire++;
      if (objetAValider.fibroses_et_processus_interstitiels_secondaire === true)
        nombreSecondaire++;
      if (objetAValider.cypho_scoliose_secondaire === true) nombreSecondaire++;
      if (objetAValider.emphyseme_primitif_secondaire === true)
        nombreSecondaire++;
      if (objetAValider.dilatations_etendues_des_bronches_secondaire === true)
        nombreSecondaire++;
      if (objetAValider.myopathies_secondaire === true) nombreSecondaire++;
      if (objetAValider.autres_atteintes_neuro_musculaires_secondaire === true)
        nombreSecondaire++;
      if (objetAValider.pneumoconioses_secondaire === true) nombreSecondaire++;
      if (objetAValider.apnees_du_sommeil_secondaire === true)
        nombreSecondaire++;
      if (objetAValider.mucoviscidose_secondaire === true) nombreSecondaire++;
      if (
        objetAValider.dysplasie_broncho_pulmonaire_du_nourrisson_secondaire ===
        true
      )
        nombreSecondaire++;
      if (objetAValider.autre_secondaire === true) nombreSecondaire++;

      if (nombreSecondaire > 2) {
        valeurNonValidePour.push(
          '• ÉTIOLOGIE DE L’INSUFFISANCE RESPIRATOIRE (Secondaire)',
        );
      }
    }

    if (
      ![null, 'jamais', 'ne_fume_plus', 'fume'].includes(objetAValider.tabac)
    ) {
      valeurNonValidePour.push('• TABAC');
    }
    if (![true, false].includes(objetAValider.antecedent_ivd)) {
      valeurNonValidePour.push('• ANTÉCÉDENT IVD');
    }
    if (![true, false].includes(objetAValider.ivd_actuelle)) {
      valeurNonValidePour.push('• IVD ACTUELLE');
    }

    if (objetAValider.spirographie_date === false) {
      valeurNonValidePour.push('• DATE DE LA SPIROGRAPHIE');
    }
    if (objetAValider.spirographie_poids === false) {
      valeurNonValidePour.push('POIDS SPIROGRAPHIE');
    }
    if (objetAValider.spirographie_taille === false) {
      valeurNonValidePour.push('TAILLE SPIROGRAPHIE');
    }
    if (objetAValider.spirographie_capacite_vitale === false) {
      valeurNonValidePour.push('CAPACITÉ VITALE SPIROGRAPHIE');
    }
    if (objetAValider.spirographie_capacite_totale === false) {
      valeurNonValidePour.push('CAPACITÉ TOTALE SPIROGRAPHIE');
    }
    if (objetAValider.spirographie_vems === false) {
      valeurNonValidePour.push('VEMS SPIROGRAPHIE');
    }

    if (![null, 'kpa', 'mmhg'].includes(objetAValider.gaz_du_sang_arteriel)) {
      valeurNonValidePour.push('• GAZ DU SANG ARTÉRIEL');
    }

    if (objetAValider.repos_en_air_mesure_1_date === false) {
      valeurNonValidePour.push('• REPOS EN AIR MESURE 1 DATE');
    }
    if (objetAValider.repos_en_air_mesure_1_ph === false) {
      valeurNonValidePour.push('REPOS EN AIR MESURE 1 PH');
    }
    if (objetAValider.repos_en_air_mesure_1_paco2 === false) {
      valeurNonValidePour.push('REPOS EN AIR MESURE 1 PACO2');
    }
    if (objetAValider.repos_en_air_mesure_1_pao2 === false) {
      valeurNonValidePour.push('REPOS EN AIR MESURE 1 PAO2');
    }
    if (objetAValider.repos_en_air_mesure_2_date === false) {
      valeurNonValidePour.push('• REPOS EN AIR MESURE 2 DATE');
    }
    if (objetAValider.repos_en_air_mesure_2_ph === false) {
      valeurNonValidePour.push('REPOS EN AIR MESURE 2 PH');
    }
    if (objetAValider.repos_en_air_mesure_2_paco2 === false) {
      valeurNonValidePour.push('REPOS EN AIR MESURE 2 PACO2');
    }
    if (objetAValider.repos_en_air_mesure_2_pao2 === false) {
      valeurNonValidePour.push('REPOS EN AIR MESURE 2 PAO2');
    }
    if (objetAValider.repos_sous_o2_et_ou_ventilation_date === false) {
      valeurNonValidePour.push('• REPOS SOUS O2 ET OU VENTILATION DATE');
    }
    if (objetAValider.repos_sous_o2_et_ou_ventilation_ph === false) {
      valeurNonValidePour.push('REPOS SOUS O2 ET OU VENTILATION PH');
    }
    if (objetAValider.repos_sous_o2_et_ou_ventilation_paco2 === false) {
      valeurNonValidePour.push('REPOS SOUS O2 ET OU VENTILATION PACO2');
    }
    if (objetAValider.repos_sous_o2_et_ou_ventilation_pao2 === false) {
      valeurNonValidePour.push('REPOS SOUS O2 ET OU VENTILATION PAO2');
    }
    if (objetAValider.a_l_effort_en_air_date === false) {
      valeurNonValidePour.push('• A L’EFFORT EN AIR DATE');
    }
    if (objetAValider.a_l_effort_en_air_ph === false) {
      valeurNonValidePour.push('A L’EFFORT EN AIR PH');
    }
    if (objetAValider.a_l_effort_en_air_paco2 === false) {
      valeurNonValidePour.push('A L’EFFORT EN AIR PACO2');
    }
    if (objetAValider.a_l_effort_en_air_pao2 === false) {
      valeurNonValidePour.push('A L’EFFORT EN AIR PAO2');
    }
    if (objetAValider.a_l_effort_en_air_sao2 === false) {
      valeurNonValidePour.push('A L’EFFORT EN AIR SAO2');
    }
    if (objetAValider.hematocrite === false) {
      valeurNonValidePour.push('HEMATOCRITE');
    }

    return valeurNonValidePour.length == 0 ? null : valeurNonValidePour;
  }

  formatFormPdf(formPdf: any): any {
    let formPdfFormate: any = { ...formPdf };

    if (formPdfFormate.nir_court == '') {
      formPdfFormate.nir_court = null;
    }
    if (formPdfFormate.nir_cle == '') {
      formPdfFormate.nir_cle = null;
    }
    if (formPdfFormate.assure_nom == '') {
      formPdfFormate.assure_nom = null;
    }
    if (formPdfFormate.assure_prenom == '') {
      formPdfFormate.assure_prenom = null;
    }
    if (formPdfFormate.assure_adresse == '') {
      formPdfFormate.assure_adresse = null;
    }
    if (formPdfFormate.caisse == '') {
      formPdfFormate.caisse = null;
    }
    if (formPdfFormate.assure_autre_detail == '') {
      formPdfFormate.assure_autre_detail = null;
    }
    if (formPdfFormate.nom == '') {
      formPdfFormate.nom = null;
    }
    if (formPdfFormate.prenom == '') {
      formPdfFormate.prenom = null;
    }

    if (formPdfFormate.oxygenotherapie_par_concentrateur_duree == '') {
      formPdfFormate.oxygenotherapie_par_concentrateur_duree = null;
    }
    if (formPdfFormate.oxygenotherapie_par_concentrateur_debit == '') {
      formPdfFormate.oxygenotherapie_par_concentrateur_debit = null;
    }
    if (formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_duree == '') {
      formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_duree = null;
    }
    if (formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_debit == '') {
      formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_debit = null;
    }
    if (formPdfFormate.oxygenotherapie_liquide_duree == '') {
      formPdfFormate.oxygenotherapie_liquide_duree = null;
    }
    if (formPdfFormate.oxygenotherapie_liquide_debit == '') {
      formPdfFormate.oxygenotherapie_liquide_debit = null;
    }
    if (formPdfFormate.ventilation_mecanique_a_l_embout_buccal_duree == '') {
      formPdfFormate.ventilation_mecanique_a_l_embout_buccal_duree = null;
    }
    if (formPdfFormate.ventilation_mecanique_a_l_embout_buccal_debit == '') {
      formPdfFormate.ventilation_mecanique_a_l_embout_buccal_debit = null;
    }
    if (formPdfFormate.ventilation_mecanique_par_masque_nasal_duree == '') {
      formPdfFormate.ventilation_mecanique_par_masque_nasal_duree = null;
    }
    if (formPdfFormate.ventilation_mecanique_par_masque_nasal_debit == '') {
      formPdfFormate.ventilation_mecanique_par_masque_nasal_debit = null;
    }
    if (formPdfFormate.ventilation_mecanique_par_tracheotomie_duree == '') {
      formPdfFormate.ventilation_mecanique_par_tracheotomie_duree = null;
    }
    if (formPdfFormate.ventilation_mecanique_par_tracheotomie_debit == '') {
      formPdfFormate.ventilation_mecanique_par_tracheotomie_debit = null;
    }
    if (
      formPdfFormate.ventilation_mecanique_par_prothese_extra_thoracique_duree ==
      ''
    ) {
      formPdfFormate.ventilation_mecanique_par_prothese_extra_thoracique_duree =
        null;
    }
    if (
      formPdfFormate.ventilation_mecanique_par_prothese_extra_thoracique_debit ==
      ''
    ) {
      formPdfFormate.ventilation_mecanique_par_prothese_extra_thoracique_debit =
        null;
    }
    if (formPdfFormate.ventilation_mecanique_par_ppc_duree == '') {
      formPdfFormate.ventilation_mecanique_par_ppc_duree = null;
    }
    if (formPdfFormate.ventilation_mecanique_par_ppc_debit == '') {
      formPdfFormate.ventilation_mecanique_par_ppc_debit = null;
    }
    if (formPdfFormate.autres_detail == '') {
      formPdfFormate.autres_detail = null;
    }
    if (formPdfFormate.association_adresse == '') {
      formPdfFormate.association_adresse = null;
    }
    if (formPdfFormate.association_forfait_soins_numero == '') {
      formPdfFormate.association_forfait_soins_numero = null;
    }
    if (formPdfFormate.association_nom == '') {
      formPdfFormate.association_nom = null;
    }
    if (formPdfFormate.concentrateur_marque == '') {
      formPdfFormate.concentrateur_marque = null;
    }
    if (formPdfFormate.concentrateur_type == '') {
      formPdfFormate.concentrateur_type = null;
    }
    if (formPdfFormate.materiel_O2_liquide_marque == '') {
      formPdfFormate.materiel_O2_liquide_marque = null;
    }
    if (formPdfFormate.materiel_O2_liquide_type == '') {
      formPdfFormate.materiel_O2_liquide_type = null;
    }
    if (formPdfFormate.respirateur_marque == '') {
      formPdfFormate.respirateur_marque = null;
    }
    if (formPdfFormate.respirateur_type == '') {
      formPdfFormate.respirateur_type = null;
    }
    if (formPdfFormate.autres_marque == '') {
      formPdfFormate.autres_marque = null;
    }
    if (formPdfFormate.autres_type == '') {
      formPdfFormate.autres_type = null;
    }
    if (formPdfFormate.prescripteur_type_autre_detail == '') {
      formPdfFormate.prescripteur_type_autre_detail = null;
    }
    if (formPdfFormate.autre_detail == '') {
      formPdfFormate.autre_detail = null;
    }
    if (formPdfFormate.cas_particuliers == '') {
      formPdfFormate.cas_particuliers = null;
    }

    formPdfFormate.accident =
      formPdfFormate.accident == 1
        ? true
        : formPdfFormate.accident == 0
          ? false
          : null;
    formPdfFormate.pensionne_de_guerre =
      formPdfFormate.pensionne_de_guerre == 1 ? true : false;
    formPdfFormate.activite_professionnelle_ou_pension =
      formPdfFormate.activite_professionnelle_ou_pension == 1
        ? true
        : formPdfFormate.activite_professionnelle_ou_pension == 0
          ? false
          : null;
    formPdfFormate.ald_sur_liste =
      formPdfFormate.ald_sur_liste == 1
        ? true
        : formPdfFormate.ald_sur_liste == 0
          ? false
          : null;
    formPdfFormate.ald_hors_liste =
      formPdfFormate.ald_hors_liste == 1
        ? true
        : formPdfFormate.ald_hors_liste == 0
          ? false
          : null;
    formPdfFormate.oxygenotherapie_par_concentrateur =
      formPdfFormate.oxygenotherapie_par_concentrateur == 1 ? true : false;
    formPdfFormate.oxygenotherapie_par_concentrateur_poste_fixe =
      formPdfFormate.oxygenotherapie_par_concentrateur_poste_fixe == 1
        ? true
        : false;
    formPdfFormate.oxygenotherapie_par_concentrateur_deambulation =
      formPdfFormate.oxygenotherapie_par_concentrateur_deambulation == 1
        ? true
        : false;
    formPdfFormate.oxygenotherapie_gazeuse_par_bouteille =
      formPdfFormate.oxygenotherapie_gazeuse_par_bouteille == 1 ? true : false;
    formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_poste_fixe =
      formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_poste_fixe == 1
        ? true
        : false;
    formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_deambulation =
      formPdfFormate.oxygenotherapie_gazeuse_par_bouteille_deambulation == 1
        ? true
        : false;
    formPdfFormate.oxygenotherapie_liquide =
      formPdfFormate.oxygenotherapie_liquide == 1 ? true : false;
    formPdfFormate.oxygenotherapie_liquide_poste_fixe =
      formPdfFormate.oxygenotherapie_liquide_poste_fixe == 1 ? true : false;
    formPdfFormate.oxygenotherapie_liquide_deambulation =
      formPdfFormate.oxygenotherapie_liquide_deambulation == 1 ? true : false;
    formPdfFormate.ventilation_mecanique_a_l_embout_buccal =
      formPdfFormate.ventilation_mecanique_a_l_embout_buccal == 1
        ? true
        : false;
    formPdfFormate.ventilation_mecanique_par_masque_nasal =
      formPdfFormate.ventilation_mecanique_par_masque_nasal == 1 ? true : false;
    formPdfFormate.ventilation_mecanique_par_tracheotomie =
      formPdfFormate.ventilation_mecanique_par_tracheotomie == 1 ? true : false;
    formPdfFormate.ventilation_mecanique_par_prothese_extra_thoracique =
      formPdfFormate.ventilation_mecanique_par_prothese_extra_thoracique == 1
        ? true
        : false;
    formPdfFormate.ventilation_mecanique_par_ppc =
      formPdfFormate.ventilation_mecanique_par_ppc == 1 ? true : false;
    formPdfFormate.aspiration_sans_ventilation_sur_tracheotomie =
      formPdfFormate.aspiration_sans_ventilation_sur_tracheotomie == 1
        ? true
        : false;
    formPdfFormate.autres = formPdfFormate.autres == 1 ? true : false;
    formPdfFormate.bronchite_chronique_principale =
      formPdfFormate.bronchite_chronique_principale == 1 ? true : false;
    formPdfFormate.lesions_mutilantes_post_tuberculeuses_principale =
      formPdfFormate.lesions_mutilantes_post_tuberculeuses_principale == 1
        ? true
        : false;
    formPdfFormate.asthme_a_dyspnee_continue_principale =
      formPdfFormate.asthme_a_dyspnee_continue_principale == 1 ? true : false;
    formPdfFormate.fibroses_et_processus_interstitiels_principale =
      formPdfFormate.fibroses_et_processus_interstitiels_principale == 1
        ? true
        : false;
    formPdfFormate.cypho_scoliose_principale =
      formPdfFormate.cypho_scoliose_principale == 1 ? true : false;
    formPdfFormate.emphyseme_primitif_principale =
      formPdfFormate.emphyseme_primitif_principale == 1 ? true : false;
    formPdfFormate.dilatations_etendues_des_bronches_principale =
      formPdfFormate.dilatations_etendues_des_bronches_principale == 1
        ? true
        : false;
    formPdfFormate.myopathies_principale =
      formPdfFormate.myopathies_principale == 1 ? true : false;
    formPdfFormate.autres_atteintes_neuro_musculaires_principale =
      formPdfFormate.autres_atteintes_neuro_musculaires_principale == 1
        ? true
        : false;
    formPdfFormate.pneumoconioses_principale =
      formPdfFormate.pneumoconioses_principale == 1 ? true : false;
    formPdfFormate.apnees_du_sommeil_principale =
      formPdfFormate.apnees_du_sommeil_principale == 1 ? true : false;
    formPdfFormate.mucoviscidose_principale =
      formPdfFormate.mucoviscidose_principale == 1 ? true : false;
    formPdfFormate.dysplasie_broncho_pulmonaire_du_nourrisson_principale =
      formPdfFormate.dysplasie_broncho_pulmonaire_du_nourrisson_principale == 1
        ? true
        : false;
    formPdfFormate.autre_principale =
      formPdfFormate.autre_principale == 1 ? true : false;
    formPdfFormate.bronchite_chronique_secondaire =
      formPdfFormate.bronchite_chronique_secondaire == 1 ? true : false;
    formPdfFormate.lesions_mutilantes_post_tuberculeuses_secondaire =
      formPdfFormate.lesions_mutilantes_post_tuberculeuses_secondaire == 1
        ? true
        : false;
    formPdfFormate.asthme_a_dyspnee_continue_secondaire =
      formPdfFormate.asthme_a_dyspnee_continue_secondaire == 1 ? true : false;
    formPdfFormate.fibroses_et_processus_interstitiels_secondaire =
      formPdfFormate.fibroses_et_processus_interstitiels_secondaire == 1
        ? true
        : false;
    formPdfFormate.cypho_scoliose_secondaire =
      formPdfFormate.cypho_scoliose_secondaire == 1 ? true : false;
    formPdfFormate.emphyseme_primitif_secondaire =
      formPdfFormate.emphyseme_primitif_secondaire == 1 ? true : false;
    formPdfFormate.dilatations_etendues_des_bronches_secondaire =
      formPdfFormate.dilatations_etendues_des_bronches_secondaire == 1
        ? true
        : false;
    formPdfFormate.myopathies_secondaire =
      formPdfFormate.myopathies_secondaire == 1 ? true : false;
    formPdfFormate.autres_atteintes_neuro_musculaires_secondaire =
      formPdfFormate.autres_atteintes_neuro_musculaires_secondaire == 1
        ? true
        : false;
    formPdfFormate.pneumoconioses_secondaire =
      formPdfFormate.pneumoconioses_secondaire == 1 ? true : false;
    formPdfFormate.apnees_du_sommeil_secondaire =
      formPdfFormate.apnees_du_sommeil_secondaire == 1 ? true : false;
    formPdfFormate.mucoviscidose_secondaire =
      formPdfFormate.mucoviscidose_secondaire == 1 ? true : false;
    formPdfFormate.dysplasie_broncho_pulmonaire_du_nourrisson_secondaire =
      formPdfFormate.dysplasie_broncho_pulmonaire_du_nourrisson_secondaire == 1
        ? true
        : false;
    formPdfFormate.autre_secondaire =
      formPdfFormate.autre_secondaire == 1 ? true : false;
    formPdfFormate.antecedent_ivd =
      formPdfFormate.antecedent_ivd == 1 ? true : false;
    formPdfFormate.ivd_actuelle =
      formPdfFormate.ivd_actuelle == 1 ? true : false;

    formPdfFormate.spirographie_poids = this.formatNumber(
      formPdfFormate.spirographie_poids,
    );
    formPdfFormate.spirographie_taille = this.formatNumber(
      formPdfFormate.spirographie_taille,
    );
    formPdfFormate.spirographie_capacite_vitale = this.formatNumber(
      formPdfFormate.spirographie_capacite_vitale,
    );
    formPdfFormate.spirographie_capacite_totale = this.formatNumber(
      formPdfFormate.spirographie_capacite_totale,
    );
    formPdfFormate.spirographie_vems = this.formatNumber(
      formPdfFormate.spirographie_vems,
    );
    formPdfFormate.repos_en_air_mesure_1_ph = this.formatNumber(
      formPdfFormate.repos_en_air_mesure_1_ph,
    );
    formPdfFormate.repos_en_air_mesure_1_paco2 = this.formatNumber(
      formPdfFormate.repos_en_air_mesure_1_paco2,
    );
    formPdfFormate.repos_en_air_mesure_1_pao2 = this.formatNumber(
      formPdfFormate.repos_en_air_mesure_1_pao2,
    );
    formPdfFormate.repos_en_air_mesure_2_ph = this.formatNumber(
      formPdfFormate.repos_en_air_mesure_2_ph,
    );
    formPdfFormate.repos_en_air_mesure_2_paco2 = this.formatNumber(
      formPdfFormate.repos_en_air_mesure_2_paco2,
    );
    formPdfFormate.repos_en_air_mesure_2_pao2 = this.formatNumber(
      formPdfFormate.repos_en_air_mesure_2_pao2,
    );
    formPdfFormate.repos_sous_o2_et_ou_ventilation_ph = this.formatNumber(
      formPdfFormate.repos_sous_o2_et_ou_ventilation_ph,
    );
    formPdfFormate.repos_sous_o2_et_ou_ventilation_paco2 = this.formatNumber(
      formPdfFormate.repos_sous_o2_et_ou_ventilation_paco2,
    );
    formPdfFormate.repos_sous_o2_et_ou_ventilation_pao2 = this.formatNumber(
      formPdfFormate.repos_sous_o2_et_ou_ventilation_pao2,
    );
    formPdfFormate.a_l_effort_en_air_ph = this.formatNumber(
      formPdfFormate.a_l_effort_en_air_ph,
    );
    formPdfFormate.a_l_effort_en_air_paco2 = this.formatNumber(
      formPdfFormate.a_l_effort_en_air_paco2,
    );
    formPdfFormate.a_l_effort_en_air_pao2 = this.formatNumber(
      formPdfFormate.a_l_effort_en_air_pao2,
    );
    formPdfFormate.a_l_effort_en_air_sao2 = this.formatNumber(
      formPdfFormate.a_l_effort_en_air_sao2,
    );
    formPdfFormate.hematocrite = this.formatNumber(formPdfFormate.hematocrite);

    formPdfFormate.date_de_prescription = this.formatDate(
      formPdfFormate.date_de_prescription,
    );
    formPdfFormate.assure_sans_emploi_date = this.formatDate(
      formPdfFormate.assure_sans_emploi_date,
    );
    formPdfFormate.accident_date = this.formatDate(
      formPdfFormate.accident_date,
    );
    formPdfFormate.date_de_naissance = this.formatDate(
      formPdfFormate.date_de_naissance,
    );
    formPdfFormate.spirographie_date = this.formatDate(
      formPdfFormate.spirographie_date,
    );
    formPdfFormate.repos_en_air_mesure_1_date = this.formatDate(
      formPdfFormate.repos_en_air_mesure_1_date,
    );
    formPdfFormate.repos_en_air_mesure_2_date = this.formatDate(
      formPdfFormate.repos_en_air_mesure_2_date,
    );
    formPdfFormate.repos_sous_o2_et_ou_ventilation_date = this.formatDate(
      formPdfFormate.repos_sous_o2_et_ou_ventilation_date,
    );
    formPdfFormate.a_l_effort_en_air_date = this.formatDate(
      formPdfFormate.a_l_effort_en_air_date,
    );

    return formPdfFormate;
  }

  formatNumber(number: string): number | null | false {
    if (number == '') {
      return null;
    }

    let numberPrepare: string = number;
    numberPrepare = numberPrepare.replace(',', '.');

    if (isNaN(Number(numberPrepare))) {
      return false;
    } else {
      return Number(numberPrepare);
    }
  }

  formatDate(dateString: string): Date | null | false {
    if (dateString == '') {
      return null;
    }
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    if (!regex.test(dateString)) {
      return false;
    }

    const [day, month, year] = dateString.split('/').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    ) {
      return date;
    } else {
      return false;
    }
  }

  canSign() {
    return this.userList.find(
      (test) => test.id == this.authService.getUser()?.id,
    );
  }
}
